
import React, { useState } from "react";
import { 
  Form, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormControl, 
  FormDescription 
} from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Plus, X } from "lucide-react";

const formSchema = z.object({
  programmeName: z.string().min(2, "Programme name is required"),
  isActive: z.boolean(),
  conversionRate: z.coerce.number().positive("Must be a positive number"),
  expiryMonths: z.coerce.number().int().positive("Must be a positive integer"),
  expiryNotification: z.string(),
  limitPointsUsage: z.boolean(),
  coopMembersOnly: z.boolean(),
  redemptionMethods: z.object({
    cashVoucher: z.boolean(),
    freeItem: z.boolean(),
    percentageDiscount: z.boolean(),
  }),
});

type FormValues = z.infer<typeof formSchema>;

const ProgrammeSetupTab = () => {
  const { toast } = useToast();
  const [bonusEvents, setBonusEvents] = useState([
    { id: 1, type: "birthday", points: 50 },
    { id: 2, type: "referral", points: 25 },
  ]);
  const [productBonuses, setProductBonuses] = useState([
    { id: 1, productId: "P12345", productName: "Paper Towels", bonusPoints: 10 },
    { id: 2, productId: "P67890", productName: "Detergent", bonusPoints: 15 },
  ]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      programmeName: "JimatMart Rewards",
      isActive: true,
      conversionRate: 10,
      expiryMonths: 12,
      expiryNotification: "1 month",
      limitPointsUsage: true,
      coopMembersOnly: false,
      redemptionMethods: {
        cashVoucher: true,
        freeItem: true,
        percentageDiscount: true,
      },
    },
  });

  const onSubmit = (values: FormValues) => {
    // In a real app, this would save to IndexedDB and sync to the server when online
    console.log("Form submitted:", values);
    toast({
      title: "Settings Saved",
      description: "Loyalty programme settings have been updated",
    });
  };

  const addBonusEvent = () => {
    const newId = bonusEvents.length + 1;
    setBonusEvents([...bonusEvents, { id: newId, type: "purchase_milestone", points: 0 }]);
  };

  const addProductBonus = () => {
    const newId = productBonuses.length + 1;
    setProductBonuses([...productBonuses, { id: newId, productId: "", productName: "", bonusPoints: 0 }]);
  };

  const removeBonusEvent = (id: number) => {
    setBonusEvents(bonusEvents.filter(event => event.id !== id));
  };

  const removeProductBonus = (id: number) => {
    setProductBonuses(productBonuses.filter(product => product.id !== id));
  };

  const updateBonusEvent = (id: number, field: string, value: string | number) => {
    setBonusEvents(
      bonusEvents.map(event => {
        if (event.id === id) {
          return { ...event, [field]: field === "points" ? Number(value) : value };
        }
        return event;
      })
    );
  };

  const updateProductBonus = (id: number, field: string, value: string | number) => {
    setProductBonuses(
      productBonuses.map(product => {
        if (product.id === id) {
          return { ...product, [field]: field === "bonusPoints" ? Number(value) : value };
        }
        return product;
      })
    );
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Loyalty Programme Setup</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="programmeName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Programme Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Activation Status</FormLabel>
                        <FormDescription>Enable or disable the loyalty programme</FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="conversionRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Points Conversion Rate</FormLabel>
                      <div className="flex items-center gap-2">
                        <div className="text-sm">RM1 = </div>
                        <FormControl>
                          <Input {...field} type="number" className="w-20" />
                        </FormControl>
                        <div className="text-sm">points</div>
                      </div>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Expiry Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="expiryMonths"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Points Expire After</FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Input {...field} type="number" className="w-20" />
                        </FormControl>
                        <div className="text-sm">months</div>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="expiryNotification"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notification Before Expiry</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select notification time" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="1 week">1 week</SelectItem>
                          <SelectItem value="1 month">1 month</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Redemption Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormLabel>Redemption Methods Allowed</FormLabel>
                <FormField
                  control={form.control}
                  name="redemptionMethods.cashVoucher"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="font-normal">Cash Voucher</FormLabel>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="redemptionMethods.freeItem"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="font-normal">Free Item</FormLabel>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="redemptionMethods.percentageDiscount"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="font-normal">Percentage Discount</FormLabel>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="limitPointsUsage"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 mt-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Limit Points Usage Per Transaction</FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Membership Eligibility</CardTitle>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="coopMembersOnly"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Apply to Coop Members Only?</FormLabel>
                        <FormDescription>Enable to restrict the programme to cooperative members only</FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <span>Bonus Points Setup</span>
                <Button type="button" size="sm" variant="outline" onClick={addBonusEvent}>
                  <Plus className="h-4 w-4 mr-2" /> Add Event
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {bonusEvents.map((event) => (
                  <div key={event.id} className="flex items-center gap-4 p-4 border rounded-md">
                    <div className="flex-1">
                      <FormLabel className="text-sm">Event Type</FormLabel>
                      <Select
                        value={event.type}
                        onValueChange={(value) => updateBonusEvent(event.id, 'type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="birthday">Birthday</SelectItem>
                          <SelectItem value="referral">Referral</SelectItem>
                          <SelectItem value="purchase_milestone">Purchase Milestone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-32">
                      <FormLabel className="text-sm">Bonus Points</FormLabel>
                      <Input
                        type="number"
                        value={event.points}
                        onChange={(e) => updateBonusEvent(event.id, 'points', e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="self-end"
                      onClick={() => removeBonusEvent(event.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <span>Product-Specific Bonus</span>
                <Button type="button" size="sm" variant="outline" onClick={addProductBonus}>
                  <Plus className="h-4 w-4 mr-2" /> Add Product
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {productBonuses.map((product) => (
                  <div key={product.id} className="flex items-end gap-4 p-4 border rounded-md">
                    <div className="flex-1">
                      <FormLabel className="text-sm">Product ID</FormLabel>
                      <Input
                        value={product.productId}
                        onChange={(e) => updateProductBonus(product.id, 'productId', e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <FormLabel className="text-sm">Product Name</FormLabel>
                      <Input
                        value={product.productName}
                        onChange={(e) => updateProductBonus(product.id, 'productName', e.target.value)}
                      />
                    </div>
                    <div className="w-32">
                      <FormLabel className="text-sm">Bonus Points</FormLabel>
                      <Input
                        type="number"
                        value={product.bonusPoints}
                        onChange={(e) => updateProductBonus(product.id, 'bonusPoints', e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeProductBonus(product.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit">Save Programme Settings</Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ProgrammeSetupTab;
